import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sparkle SVG Component
const SparkleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

// Default gallery items (shown when no items uploaded)
const defaultItems = [
  {
    id: 'default-1',
    title: 'Couleurs de Joie',
    description: 'The breath of the Holy Spirit that awakened Véri\'s professional calling. Sweeping ocean blues and radiant gold strokes reflect her profound intimacy with God. This piece is a divine invitation, capturing the boundless joy that flows when the Creator guides the artist\'s hand.',
    image_url: 'https://customer-assets.emergentagent.com/job_emerald-grace-studio/artifacts/6ngqx0h9_WhatsApp%20Image%202026-04-23%20at%2014.40.46.jpeg',
    featured: true,
  },
  {
    id: 'default-2',
    title: 'Wiener Blut',
    description: 'An energetic homage to Falco\'s legendary track. Swirling currents of vibrant crimson, deep ocean blues, and flashes of radiant gold translate the song\'s pulsating rhythm into visual form, capturing the passionate heartbeat of Vienna.',
    image_url: 'https://customer-assets.emergentagent.com/job_emerald-grace-studio/artifacts/5thoj6ia_WhatsApp%20Image%202026-04-23%20at%2014.41.20.jpeg',
  },
];

export function TheSanctuary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const response = await axios.get(`${API}/galleries/sanctuary`);
      setItems(response.data.length > 0 ? response.data : defaultItems);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setItems(defaultItems);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (item) => {
    if (!item.image_url) return null;
    if (item.image_url.startsWith('http')) return item.image_url;
    return `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`;
  };

  const featuredItem = items.find(i => i.featured) || items[0];
  const galleryItems = items.filter(i => i !== featuredItem);

  return (
    <div className="pt-20" data-testid="sanctuary-page">
      {/* Hero Section */}
      <section className="py-24 bg-emerald-900 text-white relative border-t-8 border-gold-500 overflow-hidden" data-testid="sanctuary-hero">
        {/* Pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+PHBhdGggZD0iTTIwIDBMNDAgMjBMMjAgNDBMMCAyMHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+")`
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h1 className="font-cinzel text-4xl md:text-5xl font-bold text-gold-400 mb-4 glowing-text">
              The Sacred Art Area
            </h1>
            <p className="text-emerald-100 font-light text-lg max-w-2xl mx-auto">
              A visual liturgy. Each piece is a meditation on the Holy Trinity, the Saints, and the boundless grace gifted to us.
            </p>
            <div className="mt-8 flex justify-center">
              <SparkleIcon className="w-8 h-8 text-gold-500" />
            </div>
          </div>

          {/* Featured Art Piece */}
          {featuredItem && (
            <div className="flex flex-col md:flex-row items-center bg-ocean-900/50 rounded-sm overflow-hidden holy-frame border border-gold-600/30 mb-16">
              <div className="w-full md:w-1/2 p-6 md:p-12">
                <div className="relative group cursor-pointer" onClick={() => setSelectedItem(featuredItem)}>
                  <div className="absolute -inset-1 bg-gradient-to-r from-gold-400 to-amber-600 rounded-sm blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" />
                  {getImageUrl(featuredItem) ? (
                    <img 
                      src={getImageUrl(featuredItem)} 
                      alt={featuredItem.title} 
                      className="relative w-full h-auto object-cover rounded-sm border-4 border-gold-600/50 shadow-2xl transition transform hover:scale-[1.02] duration-500"
                    />
                  ) : (
                    <div className="relative w-full h-80 bg-ocean-900 rounded-sm border-4 border-gold-600/50 flex items-center justify-center">
                      <span className="font-cinzel text-emerald-500/50">{featuredItem.placeholder}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                <span className="text-gold-400 font-cinzel text-sm tracking-widest uppercase mb-2">Featured Piece</span>
                <h3 className="font-cinzel text-3xl mb-4 text-white">{featuredItem.title}</h3>
                <p className="text-emerald-100/80 mb-6 leading-relaxed">{featuredItem.description}</p>
                <div className="border-l-2 border-gold-500 pl-4 py-1">
                  <p className="text-sm font-light text-gray-300 italic">
                    "May the grace of the Lord Jesus Christ, and the love of God, and the fellowship of the Holy Spirit be with you all."
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Gallery Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-80 bg-ocean-800/40 animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleryItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-ocean-800/40 p-4 border border-emerald-700/50 rounded-sm hover:border-gold-500/50 transition duration-300 cursor-pointer flex flex-col"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <div className="h-64 bg-ocean-900 flex items-center justify-center border border-ocean-700 relative overflow-hidden group">
                    {getImageUrl(item) ? (
                      <img 
                        src={getImageUrl(item)} 
                        alt={item.title} 
                        className="absolute inset-0 w-full h-full object-cover transition transform group-hover:scale-105 duration-500"
                      />
                    ) : (
                      <>
                        <div 
                          className="absolute inset-0 opacity-20"
                          style={{
                            backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPjxjaXJjbGUgY3g9IjQiIGN5PSI0IiByPSIxIiBmaWxsPSIjZmZmIi8+PC9zdmc+")`
                          }}
                        />
                        <span className="font-cinzel text-emerald-500/50">{item.placeholder || item.title}</span>
                      </>
                    )}
                  </div>
                  <h4 className="font-cinzel text-lg mt-4 text-gold-100">{item.title}</h4>
                  {item.description && (
                    <p className="text-sm text-emerald-100/70 mt-2 font-light leading-relaxed line-clamp-3">
                      {item.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-ocean-900 border border-gold-600/30">
          <VisuallyHidden>
            <DialogTitle>{selectedItem?.title || 'Gallery Item'}</DialogTitle>
            <DialogDescription>View details of the selected artwork</DialogDescription>
          </VisuallyHidden>
          {selectedItem && (
            <div className="grid md:grid-cols-2">
              <div className="relative h-64 md:h-[500px] bg-ocean-800">
                {getImageUrl(selectedItem) ? (
                  <img
                    src={getImageUrl(selectedItem)}
                    alt={selectedItem.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-cinzel text-emerald-500/50 text-xl">{selectedItem.placeholder || selectedItem.title}</span>
                  </div>
                )}
              </div>
              <div className="p-8 flex flex-col justify-center">
                <span className="font-cinzel text-xs uppercase tracking-[0.2em] text-gold-400 mb-2">
                  The Sanctuary
                </span>
                <h2 className="font-cinzel text-2xl text-white mb-4">{selectedItem.title}</h2>
                <p className="font-lora text-emerald-100/80 leading-relaxed">{selectedItem.description}</p>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="mt-8 btn-gold w-fit"
                  data-testid="modal-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
